import { Transition, Dialog } from '@headlessui/react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { IoMdClose } from 'react-icons/io'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../app/store'
import { closeModal } from '../../../features/two-factor-modal/twoFactorModalSlice'
import { useVerify2FAMutation } from '../../../features/2fa/twoFactorSlice'
import {
  selectCurrentUser,
  setCredentials,
} from '../../../features/auth/authSlice'



export const TwoFactorModal = () => {
  const dispatch = useDispatch()
  
  const isOpen = useSelector(
    (state: RootState) => state.twoFactorModal.isOpen
  )
  const user = useSelector(selectCurrentUser)

  // 2FA code state - simple string
  const [code, setCode] = useState<string>('')

  const inputRef = useRef<HTMLInputElement>(null)

  // Reset code when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCode('')
    }
  }, [isOpen])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('2FA Modal opened, attempting focus...')
      setTimeout(() => {
        if (inputRef.current) {
          console.log('Focusing 2FA input:', inputRef.current)
          inputRef.current.focus()
        } else {
          console.log('2FA input ref is null')
        }
      }, 100)
    } else {
      console.log('2FA Modal closed')
    }
  }, [isOpen])

  const [verify2FA, { isLoading }] = useVerify2FAMutation()

  const handleCodeChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target
    // Only allow numeric characters and limit to 6 digits
    const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6)
    setCode(numericValue)
  }

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (code.length !== 6) {
      toast.error('Please enter a complete 6-digit code')
      return
    }

    try {
      toast.loading('Verifying your code...')
      const data = await verify2FA({ code: Number(code) }).unwrap()
      toast.dismiss()
      setCode('')
      dispatch(closeModal())

      const { token, refreshToken } = data
      await dispatch(setCredentials({ user, token, refreshToken }))

      toast.success(
        'Your code is verified. Now hit the button again to proceed!',
        { duration: 10000 }
      )
    } catch (error: any) {
      console.log(error?.data.message)
      toast.dismiss()
      toast.error(error?.data.message)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      dispatch(closeModal())
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative font-dashboard w-full max-w-md transform overflow-hidden rounded-2xl bg-[#161819] px-6 pt-6 pb-8 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-medium text-white">
                    Two Factor Authentication
                  </Dialog.Title>
                  {!isLoading && (
                    <button
                      onClick={handleClose}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      <IoMdClose size={24} />
                    </button>
                  )}
                </div>

                <div className="mb-6">
                  <p className="text-sm text-white/70 mb-4">
                    Enter the 6-digit code from your authenticator app to continue.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col gap-4 mb-6">
                    <input
                      ref={inputRef}
                      type="text"
                      value={code}
                      onChange={handleCodeChange}
                      onFocus={() => console.log('2FA Input focused')}
                      onBlur={() => console.log('2FA Input blurred')}
                      onClick={() => console.log('2FA Input clicked')}
                      placeholder="123456"
                      className="w-full p-3 text-center text-lg font-medium bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary focus:outline-none transition-colors"
                      maxLength={6}
                      disabled={isLoading}
                      autoComplete="off"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    
                    {code && code.length < 6 && (
                      <p className="text-sm text-white/50 text-center">
                        {6 - code.length} more digits needed
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || code.length !== 6}
                    className="w-full py-3 px-4 bg-primary hover:bg-primary/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </button>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
